import React from 'react';
import { Row, Col, Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Label, Input, Spinner, Button } from 'reactstrap';
import classnames from 'classnames';
import { useCatalogItems } from '../hooks/useCatalogItems';
import { useSalesSelection } from '../hooks/useSalesSelection';
import PaymentForm from './PaymentForm';
import { formatCurrency } from '../../../../utils/format';

const SalesSelectionPanel = ({
    activeTab,
    toggleTab,
    onAddPayment,
    onAddItem,
    memberships = [],
    acquirers = [],
    suggestedValue = 0,
    saleDate,
    setSaleDate,
    startDate,
    setStartDate,
    discount,
    setDiscount,
    cartItems = [],
    pendingEnrollments = {},
    onOpenEnrollment = null,
    onClearEnrollment = null
}) => {
    // Dados reais do catálogo (substitui MOCK_PRODUCTS / MOCK_SERVICES)
    const { products, services, loadingCatalog } = useCatalogItems();

    const { handleSelectionChange } = useSalesSelection({ onAddItem });

    return (
        <Card className="neo-stat-card">
            <CardBody className="d-flex flex-column">
                <style>{`
                    .sales-tabs-nav {
                        border-bottom: 2px solid rgba(72, 39, 18, 0.12) !important;
                    }
                    .sales-tabs-nav .nav-link.status-tab-link {
                        color: var(--color-brand-secondary) !important;
                        font-weight: 700 !important;
                        font-size: 13.5px !important;
                        padding: 10px 20px !important;
                        transition: all 0.2s ease !important;
                        border-bottom: 3px solid transparent !important;
                        background: transparent !important;
                    }
                    .sales-tabs-nav .nav-link.status-tab-link:hover {
                        color: var(--color-brand-accent) !important;
                    }
                    .sales-tabs-nav .nav-link.status-tab-link.active {
                        color: var(--color-brand-accent) !important;
                        border-bottom: 3px solid var(--color-brand-accent) !important;
                        font-weight: 900 !important;
                    }
                `}</style>

                {/* NAV TABS */}
                <Nav tabs className="status-tabs-nav sales-tabs-nav gap-1 flex-nowrap overflow-auto no-scrollbar mb-4">
                    <NavItem>
                        <NavLink className={classnames("status-tab-link", { active: activeTab === '1' })} onClick={() => toggleTab('1')} style={{ cursor: 'pointer' }}>
                            <span>PLANO</span>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames("status-tab-link", { active: activeTab === '2' })} onClick={() => toggleTab('2')} style={{ cursor: 'pointer' }}>
                            <span>PRODUTOS</span>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink className={classnames("status-tab-link", { active: activeTab === '3' })} onClick={() => toggleTab('3')} style={{ cursor: 'pointer' }}>
                            <span>SERVIÇOS</span>
                        </NavLink>
                    </NavItem>
                </Nav>

                {/* CAMPOS GLOBAIS DA VENDA (sempre visíveis) */}
                <div className="p-3 mb-4 rounded-3 border" style={{ backgroundColor: 'var(--color-bg-canvas)', borderColor: 'rgba(72, 39, 18, 0.12)', fontFamily: 'Outfit, sans-serif' }}>
                    <Row className="g-3">
                        <Col md={4}>
                            <Label className="form-label fw-bold font-size-12 mb-1" style={{ color: 'var(--color-brand-secondary)' }}>Data da Venda</Label>
                            <Input
                                type="date"
                                className="form-control form-control-sm"
                                value={saleDate || ''}
                                onChange={(e) => setSaleDate(e.target.value)}
                            />
                            <small className="text-muted d-block mt-1 animate__animated animate__fadeIn" style={{ fontSize: '9.5px', fontWeight: '500' }}>Data retroativa para histórico</small>
                        </Col>
                        <Col md={4}>
                            <Label className="form-label fw-bold font-size-12 mb-1" style={{ color: 'var(--color-brand-secondary)' }}>Data de Início *</Label>
                            <Input
                                type="date"
                                className="form-control form-control-sm"
                                value={startDate || ''}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <small className="text-muted d-block mt-1 animate__animated animate__fadeIn" style={{ fontSize: '9.5px', fontWeight: '500' }}>Início da vigência</small>
                        </Col>
                        <Col md={4}>
                            <Label className="form-label fw-bold font-size-12 mb-1" style={{ color: 'var(--color-brand-secondary)' }}>Desconto (R$)</Label>
                            <Input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="0,00"
                                value={discount || ''}
                                onChange={(e) => setDiscount(e.target.value)}
                            />
                        </Col>
                    </Row>
                </div>

                {/* CONTEÚDO DAS TABS */}
                <TabContent activeTab={activeTab} className="flex-grow-1">

                    {/* ABA: PLANO */}
                    <TabPane tabId="1">
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label className="form-label fw-semibold text-dark">Escolha o plano *</Label>
                                <Input
                                    type="select"
                                    className="form-select"
                                    onChange={(e) => handleSelectionChange(memberships, e.target.value, 'membership')}
                                    value={cartItems.find(item => item.type === 'membership')?.idItem || ""}
                                >
                                    <option value="" disabled>Selecionar...</option>
                                    {memberships.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.title || c.name} — {c.isScholarship ? 'BOLSISTA' : formatCurrency(parseFloat(c.value || 0))}
                                        </option>
                                    ))}
                                </Input>

                                {/* EXIBIÇÃO DAS REGRAS DO PLANO SELECIONADO */}
                                {(() => {
                                    const selectedMembershipId = cartItems.find(item => item.type === 'membership')?.idItem;
                                    if (!selectedMembershipId) return null;
                                    const selectedMembership = memberships.find(m => m.id === selectedMembershipId);
                                    if (!selectedMembership) return null;

                                    const allowedDaysMap = {
                                        0: "Domingo", 1: "Segunda", 2: "Terça", 3: "Quarta", 4: "Quinta", 5: "Sexta", 6: "Sábado"
                                    };

                                    return (
                                        <div className="mt-3 p-3 rounded-3 border animate__animated animate__fadeIn" style={{ backgroundColor: 'var(--color-bg-canvas)', borderColor: 'rgba(72, 39, 18, 0.15)' }}>
                                            <h6 className="fw-bold mb-2 font-size-13" style={{ color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>
                                                <i className="mdi mdi-information-outline me-1" style={{ color: 'var(--color-brand-accent)' }}></i>
                                                Regras do Contrato: {selectedMembership.title || selectedMembership.name}
                                            </h6>
                                            <div className="d-flex flex-column gap-2 font-size-12" style={{ color: 'var(--color-brand-secondary)', fontFamily: 'Outfit, sans-serif' }}>
                                                <div className="d-flex align-items-center">
                                                    <i className="mdi mdi-calendar-check me-2 font-size-15" style={{ color: 'var(--color-brand-accent)' }}></i>
                                                    <span><strong>Frequência:</strong> {selectedMembership.maxSessionsPerWeek}x na semana</span>
                                                </div>
                                                {selectedMembership.allowedDaysOfWeek && selectedMembership.allowedDaysOfWeek.length > 0 && (
                                                    <div className="d-flex align-items-center">
                                                        <i className="mdi mdi-calendar-range me-2 font-size-15" style={{ color: 'var(--color-brand-accent)' }}></i>
                                                        <span><strong>Dias Permitidos:</strong> {selectedMembership.allowedDaysOfWeek.map(d => allowedDaysMap[d]).join(', ')}</span>
                                                    </div>
                                                )}
                                                <div className="d-flex align-items-center">
                                                    <i className="mdi mdi-clock-outline me-2 font-size-15" style={{ color: 'var(--color-brand-accent)' }}></i>
                                                    <span><strong>Duração:</strong> {selectedMembership.duration} {selectedMembership.durationType === 'monthly' ? 'meses' : 'dias'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </Col>
                        </Row>
                    </TabPane>

                    {/* ABA: PRODUTOS (dados reais do Firestore) */}
                    <TabPane tabId="2">
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label className="form-label fw-semibold">Produto</Label>
                                {loadingCatalog ? (
                                    <div className="text-muted small d-flex align-items-center gap-2">
                                        <Spinner size="sm" /> Carregando produtos...
                                    </div>
                                ) : (
                                    <Input
                                        type="select"
                                        className="form-select"
                                        onChange={(e) => handleSelectionChange(products, e.target.value, 'product')}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>
                                            {products.length === 0 ? 'Nenhum produto cadastrado' : 'Buscar produto...'}
                                        </option>
                                        {products.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name} — {formatCurrency(parseFloat(p.price))}
                                            </option>
                                        ))}
                                    </Input>
                                )}
                            </Col>
                        </Row>
                    </TabPane>

                    {/* ABA: SERVIÇOS (dados reais do Firestore) */}
                    <TabPane tabId="3">
                        <Row className="mb-3">
                            <Col md={12}>
                                <Label className="form-label fw-semibold">Serviço</Label>
                                {loadingCatalog ? (
                                    <div className="text-muted small d-flex align-items-center gap-2">
                                        <Spinner size="sm" /> Carregando serviços...
                                    </div>
                                ) : (
                                    <Input
                                        type="select"
                                        className="form-select"
                                        onChange={(e) => handleSelectionChange(services, e.target.value, 'service')}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>
                                            {services.length === 0 ? 'Nenhum serviço cadastrado' : 'Selecionar...'}
                                        </option>
                                        {services.map(s => (
                                            <option key={s.id} value={s.id}>
                                                {s.name} — {formatCurrency(parseFloat(s.price))}
                                            </option>
                                        ))}
                                    </Input>
                                )}
                            </Col>
                        </Row>
                    </TabPane>
                </TabContent>

                {/* AREA DE ALERTAS DE MATRÍCULA  - NO CARD PRINCIPAL */}
                {cartItems.filter(item => item.enrollmentRequired).length > 0 && (
                    <div className="mt-4 pt-4 border-top" style={{ borderColor: 'rgba(72, 39, 18, 0.12)' }}>
                        <h6 className="fw-bold mb-3 font-size-14" style={{ color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>
                            <i className="mdi mdi-account-multiple-plus me-2" style={{ color: 'var(--color-brand-accent)' }}></i>
                            Turmas Obrigatórias
                        </h6>

                        {cartItems.filter(item => item.enrollmentRequired).map((item) => {
                            const pendings = pendingEnrollments[item.cartId];
                            const hasPendings = pendings && pendings.length > 0;

                            return (
                                <div key={item.cartId} className="p-3 rounded-3 mb-3 border animate__animated animate__fadeIn" style={{ backgroundColor: 'var(--color-bg-canvas)', borderColor: hasPendings ? 'rgba(72, 39, 18, 0.12)' : 'var(--color-brand-accent)', boxShadow: hasPendings ? 'none' : '0 4px 12px rgba(226,102,28,0.08)' }}>
                                    <div className="d-flex justify-content-between align-items-center mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        <h6 className="m-0 fw-bold font-size-13" style={{ color: 'var(--color-brand-primary)' }}>{item.name}</h6>
                                        {hasPendings ? (
                                            <span 
                                                className="badge rounded-pill font-size-11 px-3 py-1 fw-bold"
                                                style={{ backgroundColor: 'rgba(21, 128, 61, 0.08)', color: '#15803d', border: '1px solid rgba(21, 128, 61, 0.15)' }}
                                            >
                                                <i className="mdi mdi-check-circle me-1"></i>PRONTAS ({pendings.length})
                                            </span>
                                        ) : (
                                            <span 
                                                className="badge rounded-pill font-size-11 px-3 py-1 fw-bold animate__animated animate__pulse animate__infinite"
                                                style={{ backgroundColor: 'rgba(226, 102, 28, 0.08)', color: 'var(--color-brand-accent)', border: '1px solid rgba(226, 102, 28, 0.15)' }}
                                            >
                                                <i className="mdi mdi-alert-circle me-1"></i>PENDENTE
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-muted font-size-12 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        Exige a seleção prévia das turmas associadas a este plano.
                                    </p>

                                    {!hasPendings ? (
                                        <Button
                                            color="primary"
                                            outline
                                            size="sm"
                                            className="w-100 fw-bold"
                                            onClick={() => onOpenEnrollment && onOpenEnrollment(item.cartId)}
                                            style={{ borderRadius: '8px' }}
                                        >
                                            <i className="mdi mdi-calendar-plus me-1"></i> Selecionar Turmas
                                        </Button>
                                    ) : (
                                        <div className="d-flex justify-content-between align-items-start bg-white rounded-3 p-3 border shadow-sm" style={{ borderColor: 'rgba(72, 39, 18, 0.12)' }}>
                                            <div className="text-muted font-size-12 flex-grow-1 d-flex flex-column gap-2 pe-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                {pendings.map((enr, i) => (
                                                    <div key={i} className="pb-2 border-bottom border-light last-child-no-border">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <span className="fw-bold text-dark">
                                                                {enr.activityName || "Atividade"}
                                                            </span>
                                                            <span className="text-muted font-size-11 fw-semibold">
                                                                <i className="mdi mdi-account-tie me-1 text-primary"></i>{enr.teacherName || enr.employeeName || "Professor"}
                                                            </span>
                                                        </div>
                                                        <div className="text-muted font-size-11 fw-medium">
                                                            <i className="mdi mdi-calendar-clock me-1 text-primary"></i>
                                                            <span>{enr.dayOfWeek} às {enr.startTime}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="d-flex flex-column gap-1 shrink-0">
                                                <Button color="light" size="sm" title="Editar Turmas" onClick={() => onOpenEnrollment && onOpenEnrollment(item.cartId)} style={{ borderRadius: '6px' }}>
                                                    <i className="mdi mdi-pencil text-primary"></i>
                                                </Button>
                                                <Button color="light" size="sm" title="Limpar Turmas" onClick={() => onClearEnrollment && onClearEnrollment(item.cartId)} style={{ borderRadius: '6px' }}>
                                                    <i className="mdi mdi-trash-can-outline text-danger"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
                {/* FORMULÁRIO DE PAGAMENTO (componente extraído) */}
                <PaymentForm
                    acquirers={acquirers}
                    suggestedValue={suggestedValue}
                    onAddPayment={onAddPayment}
                    onAddItem={onAddItem}
                />

            </CardBody>
        </Card>
    );
};

export default SalesSelectionPanel;
